// Aula del booking (fase 4A): orquesta los 3 estados — esperando, en curso, cerrada.
// Las credenciales reales de Agora se integran en 4B; aquí el video es un placeholder.

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Icon } from '@/components/atoms/Icon';
import { Spinner } from '@/components/atoms/Spinner';
import { ChatPanel } from '@/components/molecules/ChatPanel';
import { Header } from '@/components/organisms/Header';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/services/api';
import {
  closeClassroom,
  getClassroomFromBooking,
  joinClassroom,
  leaveClassroom,
  openClassroom,
  type AgoraToken,
  type FromBookingPayload,
} from '@/services/classrooms';
import styles from './Aula.module.scss';

const TRACKING_LABELS: Record<number, string> = {
  1: 'Present',
  2: 'Absent',
  3: 'In progress',
  4: 'Expected',
  5: 'Canceled',
};

export function Aula() {
  const { bookingId: idStr } = useParams<{ bookingId: string }>();
  const { state: authState } = useAuth();
  const bookingId = idStr ? Number(idStr) : NaN;

  const [data, setData] = useState<FromBookingPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [agora, setAgora] = useState<AgoraToken | null>(null);

  const reload = useCallback(async () => {
    if (!Number.isInteger(bookingId) || bookingId < 1) {
      setError('Booking inválido');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const d = await getClassroomFromBooking(bookingId);
      setData(d);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => { void reload(); }, [reload]);

  // Polling del estado del aula cada 8s mientras esté abierta o esperando (para que el alumno
  // detecte cuando el profe abre el aula). En 4B esto será un evento WebSocket.
  useEffect(() => {
    if (!data) return;
    const state = data.classroom?.state;
    if (state === 'closed') return;
    const iv = setInterval(() => { void reload(); }, 8000);
    return () => clearInterval(iv);
  }, [data, reload]);

  if (authState.status !== 'authenticated') return null;
  const roles = authState.user.roles;
  const isTeacher = roles.includes('teacher');
  const isAdmin = roles.includes('admin');
  const canOpen = isTeacher || isAdmin;

  if (loading) {
    return (
      <>
        <Header title="Aula" />
        <main className={styles.main}><div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}><Spinner size="lg" /></div></main>
      </>
    );
  }
  if (error || !data) {
    return (
      <>
        <Header title="Aula" />
        <main className={styles.main}>
          <Link to="/reservas" className={styles.backLink}><Icon name="chevron-left" size={16} /> Volver</Link>
          <p className={styles.error}>{error ?? 'Sin datos'}</p>
        </main>
      </>
    );
  }

  const { booking, classroom, attendance } = data;
  const state: 'scheduled' | 'open' | 'closed' = classroom?.state ?? 'scheduled';
  const scheduledDate = new Date(booking.scheduled_at);
  const endDate = new Date(scheduledDate.getTime() + booking.duration_min * 60 * 1000);

  async function onOpen() {
    setBusy(true);
    setError(null);
    try {
      await openClassroom(bookingId);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al abrir');
    } finally {
      setBusy(false);
    }
  }

  async function onClose() {
    if (!classroom) return;
    setBusy(true);
    try {
      await closeClassroom(classroom.id);
      setAgora(null);
      await reload();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al cerrar');
    } finally {
      setBusy(false);
    }
  }

  async function onJoin() {
    if (!classroom) return;
    setBusy(true);
    try {
      const token = await joinClassroom(classroom.id);
      setAgora(token);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al entrar');
    } finally {
      setBusy(false);
    }
  }

  async function onLeave() {
    if (!classroom) return;
    setBusy(true);
    try {
      await leaveClassroom(classroom.id);
      setAgora(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Error al salir');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <Header title={`Aula · ${booking.type === 'one' ? 'Individual' : 'Grupal'}`} />
      <main className={styles.main}>
        <div className={styles.leftCol}>
          <Link to="/reservas" className={styles.backLink}>
            <Icon name="chevron-left" size={16} /> Volver
          </Link>

          {state === 'scheduled' && (
            <div className={styles.waitingBox}>
              <Badge tone="accent">Esperando</Badge>
              <h2>Tu clase con {data.booking.teacher_id === authState.user.id ? 'tus alumnos' : 'el profesor'}</h2>
              <p>Programada para las <strong>{scheduledDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })}</strong> ({booking.duration_min} min).</p>
              <p>El aula se abrirá cuando el profesor pulse "Abrir aula".</p>
              {canOpen && (
                <div className={styles.controls}>
                  <Button variant="primary" onClick={() => void onOpen()} disabled={busy}>
                    {busy ? 'Abriendo…' : 'Abrir aula'}
                  </Button>
                </div>
              )}
            </div>
          )}

          {state === 'open' && classroom && (
            <div className={styles.videoBox}>
              {agora ? (
                <>
                  <div className={styles.videoPlaceholder}>
                    <div>
                      <p>Video conferencia (placeholder).</p>
                      <p>
                        Conectado al canal: <code>{agora.channelName}</code>
                        {agora.mock && <> · <span className={styles.mockBadge}>mock</span></>}
                      </p>
                      <p style={{ fontSize: 12, marginTop: 16 }}>
                        Token Agora obtenido. SDK Web v4 se integrará en fase 4B con credenciales reales.
                      </p>
                    </div>
                  </div>
                  <div className={styles.controls}>
                    <Button variant="secondary" onClick={() => void onLeave()} disabled={busy}>Salir del aula</Button>
                    {canOpen && (
                      <Button variant="primary" onClick={() => void onClose()} disabled={busy}>
                        Cerrar aula para todos
                      </Button>
                    )}
                  </div>
                </>
              ) : (
                <>
                  <div className={styles.videoPlaceholder}>
                    <div>
                      <Badge tone="success">Aula abierta</Badge>
                      <p style={{ marginTop: 12 }}>Pulsa "Entrar" para conectarte.</p>
                    </div>
                  </div>
                  <div className={styles.controls}>
                    <Button variant="primary" onClick={() => void onJoin()} disabled={busy}>
                      {busy ? 'Entrando…' : 'Entrar al aula'}
                    </Button>
                    {canOpen && (
                      <Button variant="secondary" onClick={() => void onClose()} disabled={busy}>
                        Cerrar aula
                      </Button>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {state === 'closed' && classroom && (
            <div className={styles.closedBox}>
              <h2>Aula cerrada</h2>
              <p>La clase terminó a las <strong>{classroom.closed_at ? new Date(classroom.closed_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—'}</strong>.</p>
              <p style={{ fontSize: 14, marginTop: 12 }}>El profesor podrá registrar feedback en fase 5.</p>
            </div>
          )}

          {(booking.type === 'gr' || canOpen) && (
            <section className={styles.attendance}>
              <h3>Alumnos ({attendance.length})</h3>
              <table>
                <thead>
                  <tr>
                    <th>Alumno</th>
                    <th>Entrada</th>
                    <th>Salida</th>
                    <th>Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {attendance.map((a) => (
                    <tr key={a.student_id}>
                      <td>{a.display_name}</td>
                      <td>{a.joined_at ? new Date(a.joined_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td>{a.left_at ? new Date(a.left_at).toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                      <td>{a.tracking_status_id ? TRACKING_LABELS[a.tracking_status_id] ?? a.tracking_status_slug : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <p style={{ fontSize: 11, color: '#888', marginTop: 8, fontStyle: 'italic' }}>
                Fin de aula prevista: {endDate.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' })} ·
                Estado del booking: {booking.status}
              </p>
            </section>
          )}
        </div>

        <div className={styles.rightCol}>
          <ChatPanel
            classroomId={classroom?.id ?? null}
            currentUserId={authState.user.id}
            enabled={state === 'open'}
          />
        </div>
      </main>
    </>
  );
}
